/* App Tư vấn cơ sở — lượt server gộp với phần chưa có API (báo giá, cọc, kết quả...) lưu trên máy (localStorage) */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import dayjs from 'dayjs';
import { SEED_VER, seedVisits, type LogItem, type Screening, type StaffSt, type Visit } from './logic';

/** Hàng chờ đã lấy từ server. Phiếu CSKH chưa có API nên vẫn giữ phiếu mẫu làm chỗ chờ */
const liveSeeds = (now: number) => seedVisits(now).filter((v) => v.status === 'referral');

/** Vai trò đang xem: bác sĩ chỉ thấy bước Bác sĩ khám, không thấy tiền (giả lập — chờ phân quyền thật) */
export type ViewRole = 'consultant' | 'doctor';

interface TuVanStore {
  visits: Visit[];
  seedDate?: string;
  branchId: string;
  /** Tư vấn viên đang dùng máy (ghi vào lịch sử, nhận khách) */
  me?: string;
  staffSt: Record<string, StaffSt>;
  /** Lượt đang mở gần nhất — các menu sau mở tiếp lượt này */
  cur?: string;
  /** Chia khách tự động — tắt mặc định: buổi họp 30/10 khách muốn Lễ tân chia tay trước */
  autoAssign: boolean;
  role: ViewRole;
  /** Bác sĩ đang dùng máy khi xem với vai trò bác sĩ */
  meDoctor?: string;
  /** Hiện "Hành trình trước khi đến" ở Phiếu tư vấn */
  showJourney: boolean;
  /**
   * Phiếu kiosk đã lưu trên máy trước khi nối API, theo mã lịch hẹn; chỉ còn đọc lại, phiếu mới nằm trên server
   */
  screenings: Record<string, Screening>;
  set: (p: Partial<Pick<TuVanStore, 'branchId' | 'me' | 'cur' | 'autoAssign' | 'role' | 'meDoctor' | 'showJourney'>>) => void;
  setStaffSt: (id: string, st: StaffSt) => void;
  /** Mỗi ngày thay phiếu mẫu của ngày cũ, giữ lượt thật */
  ensureSeed: (now: number) => void;
  reseed: (now: number) => void;
  add: (v: Visit[]) => void;
  /** Sửa một lượt; kèm dòng lịch sử nếu có */
  update: (id: string, fn: (v: Visit) => Partial<Visit>, log?: Omit<LogItem, 'at'> & { /** Việc đã xảy ra trước đó: chèn đúng vị trí theo giờ */ at?: number }) => void;
}

export const useTuVan = create<TuVanStore>()(
  persist(
    (set, get) => ({
      visits: [],
      branchId: 'q1',
      staffSt: {},
      autoAssign: false,
      role: 'consultant',
      showJourney: true,
      screenings: {},
      set: (p) => set(p),
      setStaffSt: (id, st) => set((s) => ({ staffSt: { ...s.staffSt, [id]: st } })),
      ensureSeed: (now) => {
        const day = `${dayjs(now).format('YYYY-MM-DD')}#${SEED_VER}`;
        if (get().seedDate === day) return;
        set((s) => ({ seedDate: day, visits: [...liveSeeds(now), ...s.visits.filter((v) => !v.seed)] }));
      },
      reseed: (now) => {
        set({ seedDate: `${dayjs(now).format('YYYY-MM-DD')}#${SEED_VER}`, visits: liveSeeds(now), cur: undefined });
      },
      add: (v) => set((s) => ({ visits: [...v, ...s.visits] })),
      update: (id, fn, log) => set((s) => ({
        visits: s.visits.map((v) => (v.id === id
          ? { ...v, ...fn(v), log: log ? (log.at ? [{ ...log, at: log.at }, ...v.log].sort((a, b) => b.at - a.at) : [{ ...log, at: Date.now() }, ...v.log]) : v.log }
          : v)),
      })),
    }),
    {
      name: 'khothi-app-tuvan-v1',
      version: 3,
      // v2: chia tự động tắt mặc định (Lễ tân chia tay trước), thêm vai trò xem và khai báo ban đầu
      migrate: (s: any, v) => {
        const a = v < 2 ? { ...s, autoAssign: false, role: 'consultant', showJourney: true, screenings: {} } : s;
        if (v >= 3) return a;
        // v3: quy ước giới tính chung với API, Nữ từ 0 đổi sang 2; phiếu đang mở trên máy cũng đổi
        const fix = <T extends { gender?: number } | undefined>(x: T): T => (x?.gender === 0 ? { ...x, gender: 2 } : x);
        return { ...a, visits: (a.visits ?? []).map((x: any) => ({ ...x, cust: fix(x.cust), screening: fix(x.screening) })) };
      },
    },
  ),
);
